// Role-based theme configuration
// CREATED: Centralized theme system for consistent styling across all roles
// Defines colors, gradients, and visual identity for each user role
// Used by: AppSidebar, UserMenu, ProfileBottomSheet, and all role-specific components
//
// IMPORTANT: Role colors are ONLY used for identification purposes:
// - User avatars and initials
// - Role badges
// - Subtle role indicators
//
// DO NOT USE for: CTAs, buttons, main navigation (use primary color instead)
// CHANGE: UserRole now imported from supabase.ts (single source of truth), re-exported for convenience

import type { UserRole } from './supabase';
export type { UserRole };

export interface RoleTheme {
  name: string;
  displayName: string;
  // Main colors - ONLY use for identification elements (avatars, badges)
  primary: string;
  primaryHover: string;
  primaryLight: string;
  primaryDark: string;
  // Gradients - For decorative role identification
  gradient: string;
  gradientHover: string;
  // Avatar/Badge colors - Primary use case for role colors
  avatarGradient: string;
  badgeBg: string;
  badgeText: string;
  badgeBorder: string;
  // Navigation colors - Using global primary color for consistency
  navItemActive: string;
  navItemActiveText: string;
  navItemHover: string;
  // Sidebar colors
  sidebarBorder: string;
}

export const roleThemes: Record<UserRole, RoleTheme> = {
  admin: {
    name: 'admin',
    displayName: 'Administrateur',
    primary: 'rgb(244, 63, 94)', // rose-500
    primaryHover: 'rgb(225, 29, 72)', // rose-600
    primaryLight: 'rgb(254, 242, 242)', // rose-50
    primaryDark: 'rgb(159, 18, 57)', // rose-900
    gradient: 'from-rose-200 to-pink-200',
    gradientHover: 'from-rose-600 to-pink-700',
    avatarGradient: 'from-rose-400 to-pink-600',
    badgeBg: 'bg-rose-100',
    badgeText: 'text-rose-800',
    badgeBorder: 'border-rose-200',
    navItemActive: 'bg-rose-50',
    navItemActiveText: 'text-rose-600',
    navItemHover: 'hover:bg-rose-50',
    sidebarBorder: 'border-rose-100'
  },
  director: {
    name: 'director',
    displayName: 'Directeur',
    primary: 'rgb(245, 158, 11)', // amber-500
    primaryHover: 'rgb(217, 119, 6)', // amber-600
    primaryLight: 'rgb(254, 252, 232)', // amber-50
    primaryDark: 'rgb(120, 53, 15)', // amber-900
    gradient: 'from-amber-200 to-orange-200',
    gradientHover: 'from-amber-600 to-orange-700',
    avatarGradient: 'from-amber-400 to-orange-600',
    badgeBg: 'bg-amber-100',
    badgeText: 'text-amber-800',
    badgeBorder: 'border-amber-200',
    navItemActive: 'bg-amber-50',
    navItemActiveText: 'text-amber-600',
    navItemHover: 'hover:bg-amber-50',
    sidebarBorder: 'border-amber-100'
  },
  manager: {
    name: 'manager',
    displayName: 'Manager',
    primary: 'rgb(16, 185, 129)', // emerald-500
    primaryHover: 'rgb(5, 150, 105)', // emerald-600
    primaryLight: 'rgb(236, 253, 245)', // emerald-50
    primaryDark: 'rgb(6, 78, 59)', // emerald-900
    gradient: 'from-emerald-200 to-teal-200',
    gradientHover: 'from-emerald-600 to-teal-700',
    avatarGradient: 'from-emerald-400 to-emerald-600',
    badgeBg: 'bg-emerald-100',
    badgeText: 'text-emerald-800',
    badgeBorder: 'border-emerald-200',
    navItemActive: 'bg-emerald-50',
    navItemActiveText: 'text-emerald-600',
    navItemHover: 'hover:bg-emerald-50',
    sidebarBorder: 'border-emerald-100'
  },
  operator: {
    name: 'operator',
    displayName: 'Opérateur',
    primary: 'rgb(59, 130, 246)', // blue-500
    primaryHover: 'rgb(37, 99, 235)', // blue-600
    primaryLight: 'rgb(239, 246, 255)', // blue-50
    primaryDark: 'rgb(30, 58, 138)', // blue-900
    gradient: 'from-blue-200 to-indigo-200',
    gradientHover: 'from-blue-600 to-indigo-700',
    avatarGradient: 'from-blue-400 to-blue-600',
    badgeBg: 'bg-blue-100',
    badgeText: 'text-blue-800',
    badgeBorder: 'border-blue-200',
    navItemActive: 'bg-blue-50',
    navItemActiveText: 'text-blue-600',
    navItemHover: 'hover:bg-blue-50',
    sidebarBorder: 'border-blue-100'
  }
};

export function getRoleTheme(role: UserRole): RoleTheme {
  return roleThemes[role];
}

export function getRoleDisplayName(role: UserRole): string {
  return roleThemes[role].displayName;
}
